import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Token-authenticated candidate-facing AI interview page.
// v1 uses browser-native Web Speech API for TTS + STT to keep vendor cost at $0.
// The avatar slot is a placeholder pulse — when HeyGen / Anam / MuseTalk is wired,
// only the <AvatarPanel/> component changes; everything else stays put.

const PHASE = {
  WELCOME: "welcome",        // intro + name confirmation
  PERMISSIONS: "permissions",// mic/camera grant
  INTERVIEW: "interview",    // active Q&A
  UPLOADING: "uploading",    // recording -> server
  DONE: "done",              // thank-you screen
};

const AiInterview = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [phase, setPhase] = useState(PHASE.WELCOME);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [transcript, setTranscript] = useState(""); // current answer being spoken
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [done, setDone] = useState(false);

  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const answerStartRef = useRef(null);

  // ---- Load session ----
  useEffect(() => {
    axios.get(`/ai/interview/public/${token}`)
      .then((r) => setSession(r.data))
      .catch(() => setLoadError("This interview link is invalid or has expired."));
  }, [token]);

  // ---- TTS helper ----
  const speak = (text) => new Promise((resolve) => {
    if (!("speechSynthesis" in window)) { resolve(); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.onstart = () => setAiSpeaking(true);
    u.onend = () => { setAiSpeaking(false); resolve(); };
    u.onerror = () => { setAiSpeaking(false); resolve(); };
    window.speechSynthesis.speak(u);
  });

  // ---- STT helper ----
  const startListening = () => new Promise((resolve) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { resolve(""); return; }
    const r = new SR();
    r.lang = "en-US";
    r.continuous = true;
    r.interimResults = true;
    let finalText = "";
    let silenceTimer;
    const resetSilence = () => {
      clearTimeout(silenceTimer);
      // 3.5 seconds of no new speech ends the answer
      silenceTimer = setTimeout(() => { try { r.stop(); } catch (e) {} }, 3500);
    };
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript + " ";
        else interim += e.results[i][0].transcript;
      }
      setTranscript((finalText + interim).trim());
      resetSilence();
    };
    r.onerror = () => { try { r.stop(); } catch (e) {} };
    r.onend = () => {
      clearTimeout(silenceTimer);
      setListening(false);
      resolve(finalText.trim());
    };
    recognitionRef.current = r;
    setListening(true);
    setTranscript("");
    r.start();
    resetSilence();
  });

  // ---- Permissions + recorder setup ----
  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      mediaStreamRef.current = stream;
      // pick a supported mime; webm/vp8 is widely supported
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      rec.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      recorderRef.current = rec;
      rec.start(1000);
      setPhase(PHASE.INTERVIEW);
      runInterviewLoop();
    } catch (e) {
      alert("We need camera and microphone access to run the interview. Please allow access and refresh.");
    }
  };

  // ---- Main loop ----
  const runInterviewLoop = async () => {
    const questions = session?.questions || [];
    await speak(`Hello ${session?.candidateName || "there"}. I'm your AI interviewer. I'll ask you ${questions.length} questions. Please answer naturally — I'll wait until you're done. Let's begin.`);
    for (let i = 0; i < questions.length; i++) {
      setQuestionIdx(i);
      const q = questions[i];
      await speak(q.text);
      answerStartRef.current = Date.now();
      const answer = await startListening();
      const durationSec = Math.round((Date.now() - answerStartRef.current) / 1000);
      try {
        await axios.post(`/ai/interview/public/${token}/answer`, {
          questionIdx: q.idx,
          answerText: answer,
          durationSec,
        });
      } catch (e) { /* swallow — will still upload final recording */ }
    }
    await speak("Thank you. That concludes the interview. Please wait a moment while we save your responses.");
    finishInterview();
  };

  const finishInterview = async () => {
    setPhase(PHASE.UPLOADING);
    // Stop recorder
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      await new Promise((resolve) => {
        recorderRef.current.onstop = resolve;
        recorderRef.current.stop();
      });
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
    const fd = new FormData();
    fd.append("file", blob, "interview.webm");
    try {
      await axios.post(`/ai/interview/public/${token}/recording`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (e) { /* still proceed to thank-you */ }
    setDone(true);
    setPhase(PHASE.DONE);
  };

  // ---- Render ----
  if (loadError) return <CenteredCard><h1 className="text-xl font-bold text-ah-red mb-2">Link invalid</h1><p>{loadError}</p></CenteredCard>;
  if (!session) return <CenteredCard><p>Loading…</p></CenteredCard>;

  const questions = session.questions || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F6FA] to-[#EEE9FF] flex items-center justify-center p-6">
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-xl overflow-hidden border border-[#E4E5EF]">
        {/* Header */}
        <div className="px-8 py-5 bg-gradient-to-r from-ah-navy to-[#3D2E96] text-white flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Ample Hire AI Interview</h1>
            {session.jobTitle && <p className="text-xs opacity-80 mt-0.5">For: {session.jobTitle}</p>}
          </div>
          <div className="text-xs opacity-80">
            Hi, {session.candidateName || "Candidate"}
          </div>
        </div>

        {/* Body — branches by phase */}
        {phase === PHASE.WELCOME && (
          <div className="p-10 text-center">
            <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-ah-navy to-[#3D2E96] flex items-center justify-center text-white">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3"/></svg>
            </div>
            <h2 className="text-2xl font-bold text-[#0D0B1E]">Welcome to your interview</h2>
            <p className="text-sm text-[#5C5E78] mt-2 max-w-lg mx-auto">
              You'll be interviewed by our AI interviewer. It will ask you {questions.length} question{questions.length === 1 ? "" : "s"}.
              Speak naturally — the AI will wait for you to finish before moving on. The session is recorded.
            </p>
            <div className="mt-6 inline-block text-left bg-[#FAFAFC] border border-[#E4E5EF] rounded-xl px-5 py-4 text-xs text-[#5C5E78]">
              <p>✓ Find a quiet, well-lit space</p>
              <p>✓ We need camera and microphone access</p>
              <p>✓ The interview cannot be paused once started</p>
            </div>
            <div className="mt-7">
              <button
                onClick={() => { setPhase(PHASE.PERMISSIONS); requestPermissions(); }}
                className="bg-ah-red hover:bg-[#B5002D] text-white font-semibold px-8 py-3 rounded-xl transition shadow-md"
              >
                Start interview
              </button>
            </div>
          </div>
        )}

        {phase === PHASE.PERMISSIONS && (
          <div className="p-10 text-center text-sm text-[#5C5E78]">Requesting camera & microphone access…</div>
        )}

        {phase === PHASE.INTERVIEW && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <AvatarPanel speaking={aiSpeaking} listening={listening} />
            <div className="p-8 flex flex-col">
              <div className="text-xs text-[#9496AD] mb-2">
                Question {questionIdx + 1} of {questions.length}
              </div>
              <div className="text-lg font-semibold text-[#0D0B1E] leading-snug min-h-[80px]">
                {questions[questionIdx]?.text}
              </div>
              <div className="mt-4 flex-1 bg-[#FAFAFC] border border-[#E4E5EF] rounded-xl p-4 overflow-y-auto">
                <div className="text-[10px] uppercase tracking-wider text-[#9496AD] mb-1">Your answer</div>
                <div className="text-sm text-[#0D0B1E] whitespace-pre-wrap min-h-[80px]">
                  {transcript || (listening ? <span className="text-[#9496AD] italic">Listening…</span> : <span className="text-[#9496AD] italic">Wait for the AI to finish speaking…</span>)}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className={`flex items-center gap-1.5 ${listening ? "text-ah-red" : "text-[#9496AD]"}`}>
                  <span className={`w-2 h-2 rounded-full ${listening ? "bg-ah-red animate-pulse" : "bg-[#E4E5EF]"}`}/>
                  {listening ? "Recording your answer" : "Idle"}
                </span>
                {listening && (
                  <button
                    onClick={() => recognitionRef.current?.stop()}
                    className="text-ah-navy hover:underline"
                  >
                    Done answering
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {phase === PHASE.UPLOADING && (
          <div className="p-10 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-[#EEE9FF] border-t-ah-navy animate-spin"/>
            <p className="text-sm text-[#5C5E78]">Saving your interview…</p>
          </div>
        )}

        {phase === PHASE.DONE && (
          <div className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#DFF7E6] flex items-center justify-center text-[#0F7A39]">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h2 className="text-2xl font-bold text-[#0D0B1E]">All done!</h2>
            <p className="text-sm text-[#5C5E78] mt-2 max-w-md mx-auto">
              Thank you for completing the interview. Your responses have been submitted.
              The hiring team will review them and reach out if you're shortlisted.
            </p>
          </div>
        )}
      </div>
      <p className="absolute bottom-3 text-[10px] text-[#9496AD]">
        Powered by Ample Hire AI · v1 (browser TTS / STT)
      </p>
    </div>
  );
};

// === Vendor-swappable avatar slot ===
// When you wire HeyGen / Anam / MuseTalk, replace the inside of this component
// with the vendor's <Avatar/> SDK component. The parent contract is just two props:
//   speaking: boolean   listening: boolean
const AvatarPanel = ({ speaking, listening }) => (
  <div className="bg-gradient-to-br from-[#1C1444] to-[#2B1F6B] flex items-center justify-center p-8 min-h-[360px]">
    <div className="text-center text-white">
      <div className={`relative w-48 h-48 mx-auto rounded-full bg-white/5 border-4 border-white/10 flex items-center justify-center transition ${speaking ? "scale-105" : ""}`}>
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 21v-1a8 8 0 0 1 16 0v1"/>
        </svg>
        {speaking && (
          <>
            <span className="absolute inset-0 rounded-full border-4 border-white/20 animate-ping"/>
            <span className="absolute inset-3 rounded-full border-2 border-white/10 animate-ping [animation-delay:300ms]"/>
          </>
        )}
        {listening && !speaking && (
          <span className="absolute inset-0 rounded-full border-4 border-ah-red/40 animate-pulse"/>
        )}
      </div>
      <p className="mt-4 text-sm font-semibold">Maya · AI Interviewer</p>
      <p className="text-[11px] opacity-70 mt-1">
        {speaking ? "Speaking…" : listening ? "Listening…" : "Ready"}
      </p>
    </div>
  </div>
);

const CenteredCard = ({ children }) => (
  <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center p-6">
    <div className="bg-white rounded-2xl shadow-md border border-[#E4E5EF] p-8 max-w-md w-full text-center">{children}</div>
  </div>
);

export default AiInterview;
